import { useEffect } from "react";
import { useCurrentUser } from "src/business-hooks/useCurrentUser";
import { notify } from "@utils/notification.ts";

/**
 * Enables copying URL with tenant ID via keyboard shortcut (super admin only).
 * Press Shift+D, Shift+D, Shift+S (DDS) within 2 seconds to copy current URL with ?tid=${tenant_id}.
 */
export function useShareWithTenant() {
  const currentUser = useCurrentUser();

  useEffect(() => {
    if (!currentUser.is_super_admin) {
      return;
    }

    const sequence = ["D", "D", "S"];
    let index = 0;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const resetSequence = () => {
      index = 0;
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };

    const onKeydown = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.key !== sequence[index]) {
        resetSequence();
        return;
      }

      index++;

      if (index === sequence.length) {
        const url = new URL(window.location.href);
        if (currentUser.tenant_id) {
          url.searchParams.set("tid", String(currentUser.tenant_id));
        }
        navigator.clipboard.writeText(url.toString());
        notify("URL with tenant ID copied to clipboard", {
          variant: "success",
        });
        resetSequence();
        return;
      }

      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(resetSequence, 2000);
    };

    window.addEventListener("keydown", onKeydown);
    return () => {
      window.removeEventListener("keydown", onKeydown);
      resetSequence();
    };
  }, [currentUser.is_super_admin, currentUser.tenant_id]);
}
